import { message } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useResendSmsOtpForUpdateEmailMutation } from '../../../api/mutations/resendSmsOTPForUpdateEmail';
import { useValidateSmsOtpForUpdateEmailMutation } from '../../../api/mutations/validateSmsOTPForUpdateEmail';
import LoginContent from '../../../layouts/LoginLayout/LoginContent';
import useHandleError from '../../../utilities/useHandleError';
import OTPForm, { type OTPFormValues } from '../OTPForm';
import { type ActionHandlers } from './index';

export type SmsOTPProps = { actions: ActionHandlers; token: string };

const SmsOtp = ({ actions, token }: SmsOTPProps) => {
    const { t } = useTranslation(['signInPage']);
    const [validate] = useValidateSmsOtpForUpdateEmailMutation();
    const [resend] = useResendSmsOtpForUpdateEmailMutation();

    const onSubmit = useHandleError(
        async ({ code }: OTPFormValues) => {
            message.loading({
                type: 'loading',
                content: t('signInPage:smsOtpStep.submittingMessage'),
                key: 'primary',
                duration: 0,
            });

            const { data } = await validate({ variables: { token, code: code.join('') } }).finally(() => {
                message.destroy('primary');
            });

            if (data?.validateSmsOTPForUpdateEmail?.verified) {
                actions.moveToUpdateEmail(data.validateSmsOTPForUpdateEmail.token);
            }
        },
        [actions, t, token, validate]
    );

    const resendCode = useCallback(() => {
        resend({ variables: { token } })
            .then(({ data }) => actions.moveToSmsOTP(data.resendSmsOTPForUpdateEmail.token))
            .catch(() => message.error(t('otpPage:errors.failedToResend')));
    }, [actions, resend, t, token]);

    return (
        <LoginContent description={t('signInPage:smsOtpStep.description')} title={t('signInPage:smsOtpStep.title')}>
            <OTPForm handleSubmit={onSubmit} resendCode={resendCode} />
        </LoginContent>
    );
};

export default SmsOtp;
